import type { WoodPalette } from '@bn-games/render-helpers';
import type { Theme } from '../settings/themes.js';

/**
 * Canvas-render palette consumed by every mahjong renderer. A flat token list
 * built from `Theme` so renderers never reach into the theme structure
 * directly — keeps the theme schema independent of the render side (mirrors
 * domino's `RenderPalette`).
 *
 * Suit/honor face ART comes from a composited sprite (`tileSprites.ts`), not
 * a themeable ink — only the corner numeral/wind-letter overlay is a palette
 * color (`cornerNumeralColor`, always red, the universal tile-face convention).
 */
export interface RenderPalette {
  readonly name: string;

  /** Wood palette consumed by `getWoodPattern` / `getBorderWoodPattern` — the table + backdrop. */
  readonly wood: WoodPalette;

  readonly tileFaceBase: string;
  readonly tileFaceHighlight: string;
  readonly tileFaceShadow: string;
  readonly tileFaceBorder: string;

  readonly tileBackBase: string;
  readonly tileBackPattern: string;
  readonly tileBackBorder: string;

  /** Corner rank numeral + wind-letter ink — always red, the universal tile-face convention. */
  readonly cornerNumeralColor: string;

  /** Seat-compass label. */
  readonly compassText: string;

  readonly selectedRing: string;
  readonly claimGlow: string;
  readonly hoverGlow: string;
}

export function themeToRenderPalette(theme: Theme): RenderPalette {
  return {
    name: theme.name,

    wood: {
      name: theme.name,
      boardWoodBase: theme.boardWoodBase,
      boardWoodDark: theme.boardWoodDark,
      boardWoodLight: theme.boardWoodLight,
      borderWoodBase: theme.borderWoodBase,
      borderWoodDark: theme.borderWoodDark,
      borderWoodLight: theme.borderWoodLight,
    },

    tileFaceBase: theme.tileFaceBase,
    tileFaceHighlight: theme.tileFaceHighlight,
    tileFaceShadow: theme.tileFaceShadow,
    tileFaceBorder: theme.tileFaceBorder,

    tileBackBase: theme.tileBackBase,
    tileBackPattern: theme.tileBackPattern,
    tileBackBorder: theme.tileBackBorder,

    cornerNumeralColor: theme.dragonRedColor,

    compassText: theme.compassText,

    selectedRing: theme.selectedRing,
    claimGlow: theme.validClaimGlow,
    hoverGlow: theme.selectedRing,
  };
}
